import { Box, Image, type BoxStyle } from "../../../../../../mads";

import { colors } from "@indeed/ifl-tokens";
import { shadows } from "@indeed/ifl-tokens/react-native";
import React, { type PropsWithChildren } from "react";
import { StyleSheet } from "react-native";

export type LogoProps = {
  /**
   * The URL of the Logo IMG.
   */
  url: string;
  /**
   * The accessibility text of the img for screen readers.
   */
  altText: string;
  /**
   * The size of the logo.
   */
  size: 32 | 56 | 60 | 64;
  /**
   * Whether the logo should be surrounded with a shadow, making it look like it is elevated from the page.
   */
  hasShadow: boolean;
  /**
   * Selects the shadow treatment when `hasShadow` is enabled.
   */
  shadowLevel?: "default" | "low";
  /**
   * Whether the logo should be surrounded with a border.
   */
  hasBorder: boolean;
};

/**
 * Used to render a square logo.
 */
export const Logo = (props: LogoProps) => {
  let sizeStyle;
  switch (props.size) {
    case 32:
      sizeStyle = styles.small;
      break;
    case 56:
      sizeStyle = styles.desktopBranded;
      break;
    case 60:
      sizeStyle = styles.medium;
      break;
    case 64:
      sizeStyle = styles.large;
      break;
  }

  return (
    <LogoShadow
      style={sizeStyle}
      hasShadow={props.hasShadow}
      shadowLevel={props.shadowLevel ?? "default"}
    >
      <Image
        source={{ uri: props.url }}
        style={[sizeStyle, props.hasBorder && styles.border]}
        accessibilityLabel={props.altText}
        testID="company-logo"
      />
    </LogoShadow>
  );
};

type LogoShadowProps = {
  hasShadow: boolean;
  shadowLevel: "default" | "low";
  style: BoxStyle;
};

const LogoShadow = (props: PropsWithChildren<LogoShadowProps>) => {
  if (!props.hasShadow) {
    return props.children;
  }

  return (
    <Box
      testID="company-logo-shadow"
      sx={[props.shadowLevel === "low" ? styles.lowShadow : styles.logoShadow, props.style]}
    >
      {props.children}
    </Box>
  );
};

const styles = StyleSheet.create({
  small: {
    width: 32,
    height: 32,
    borderRadius: 8,
  },
  desktopBranded: {
    width: 56,
    height: 56,
    borderRadius: 12,
  },
  medium: {
    width: 60,
    height: 60,
    borderRadius: 8,
  },
  large: {
    width: 64,
    height: 64,
    borderRadius: 12,
  },
  logoShadow: {
    // shadow
    borderColor: "#ffffff",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 4,
    elevation: 10,
  },
  lowShadow: {
    boxShadow: shadows.low,
  },
  border: {
    borderColor: colors.background["[base]"],
    borderWidth: 2,
  },
});
